import axios, { AxiosResponse } from "axios"
import { Dispatch, RNBThunkAction } from "../store.config"
import { AnyAction } from "redux"
import { AccountingSoftware, buyOrSell, BuyOrSell } from "../../utils/company"

export const vatTypesArray = [
  "INTRACO",
  "BTP",
  "INTRACO_GOODS",
  "WORLD",
  "WORLD_GOODS",
  "FUEL",
  "REGULAR_BUY_GOODS",
  "REGULAR_BUY_SERVICES",
  "REGULAR_SELL_GOODS",
  "REGULAR_SELL_SERVICES",
] as const

export type VatTypeEnum = (typeof vatTypesArray)[number]

export interface VatTag {
  id: number
  vat_rate_id: number
  vat_rate: number
  fiduciary_id: number | null
  company_id: number | null
  name_tag: string
  vat_type: VatTypeEnum
  created_at: string
  events: VatTagEvents[]
}
export interface QuadratusLabelTag {
  id: number
  fiduciary_id: number | null
  company_id: number | null
  label: string
  label_code: string & { length: 1 }
  buy_or_sell: BuyOrSell
  document_type: "invoice" | "credit_note"
  created_at: string
  events: VatTagEvents[]
}
export interface VatTagEvents {
  id: number
  email: string
  metadata: { [index: string]: string }
  event_type: string
  created_at: string
}

export interface CreateVatTagPayload {
  name_tag: string
  vat_type: VatTypeEnum
  vat_rate_id: number
}

export interface CreateLabelTagPayload {
  label_code: string
  label: string
  buy_or_sell: buyOrSell
  document_type: "invoice" | "credit_note"
}

interface CreateVatTagError {
  message: string
  data: { name_tag: string; vat_type: string; vat_rate_id: number }
}

interface CreateLabelTagError {
  message: string
  data: {
    label_code: string
    label: string
    buy_or_sell: string
    document_type: string
  }
}

interface CreateVatTagResponse {
  status: number
  message: string | CreateVatTagError[] | CreateLabelTagError[]
}
interface GetVatTagsResponse {
  length_limit: number
  data: VatTag[] | QuadratusLabelTag[]
}

export const enum VatTagActionEnum {
  GET_FIDUCIARY_VAT_TAGS_ATTEMPT = "VAT_TAGS/getFiduciaryVatTagsAttempt",
  GET_FIDUCIARY_VAT_TAGS_SUCCESS = "VAT_TAGS/getFiduciaryVatTagsSuccess",
  GET_FIDUCIARY_VAT_TAGS_FAILURE = "VAT_TAGS/getFiduciaryVatTagsFailure",

  GET_COMPANY_VAT_TAGS_ATTEMPT = "VAT_TAGS/getCompanyVatTagsAttempt",
  GET_COMPANY_VAT_TAGS_SUCCESS = "VAT_TAGS/getCompanyVatTagsSuccess",
  GET_COMPANY_VAT_TAGS_FAILURE = "VAT_TAGS/getCompanyVatTagsFailure",

  CREATE_VAT_TAGS_ATTEMPT = "VAT_TAGS/createVatTagsAttempt",
  CREATE_VAT_TAGS_RESET = "VAT_TAGS/createVatTagsReset",
  CREATE_VAT_TAGS_SUCCESS = "VAT_TAGS/createVatTagsSuccess",
  CREATE_VAT_TAGS_FAILURE = "VAT_TAGS/createVatTagsFailure",
  CREATE_VAT_TAGS_PARTIAL_SUCCESS = "VAT_TAGS/createVatTagsPartialSuccess",

  DESTROY_VAT_TAG_ATTEMPT = "VAT_TAGS/destroyVatTagAttempt",
  DESTROY_VAT_TAG_SUCCESS = "VAT_TAGS/destroyVatTagSuccess",
  DESTROY_VAT_TAG_FAILURE = "VAT_TAGS/destroyVatTagFailure",

  GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_ATTEMPT = "VAT_TAGS/getQuadratusFiduciaryLabelTagsAttempt",
  GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_SUCCESS = "VAT_TAGS/getQuadratusFiduciaryLabelTagsSuccess",
  GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_FAILURE = "VAT_TAGS/getQuadratusFiduciaryLabelTagsFailure",

  GET_QUADRATUS_COMPANY_LABEL_TAGS_ATTEMPT = "VAT_TAGS/getQuadratusCompanyLabelTagsAttempt",
  GET_QUADRATUS_COMPANY_LABEL_TAGS_SUCCESS = "VAT_TAGS/getQuadratusCompanyLabelTagsSuccess",
  GET_QUADRATUS_COMPANY_LABEL_TAGS_FAILURE = "VAT_TAGS/getQuadratusCompanyLabelTagsFailure",

  CREATE_QUADRATUS_LABEL_TAGS_ATTEMPT = "VAT_TAGS/createQuadratusLabelTagsAttempt",
  CREATE_QUADRATUS_LABEL_TAGS_SUCCESS = "VAT_TAGS/createQuadratusLabelTagsSuccess",
  CREATE_QUADRATUS_LABEL_TAGS_FAILURE = "VAT_TAGS/createQuadratusLabelTagsFailure",
  CREATE_QUADRATUS_LABEL_TAGS_PARTIAL_SUCCESS = "VAT_TAGS/createQuadratusLabelTagsPartialSuccess",
}

export const getFiduciaryVatTagsAttempt = () =>
  ({ type: VatTagActionEnum.GET_FIDUCIARY_VAT_TAGS_ATTEMPT } as const)
export const getFiduciaryVatTagsSuccess = (vatTags: GetVatTagsResponse) =>
  ({
    type: VatTagActionEnum.GET_FIDUCIARY_VAT_TAGS_SUCCESS,
    vatTags,
  } as const)
export const getFiduciaryVatTagsFailure = () =>
  ({ type: VatTagActionEnum.GET_FIDUCIARY_VAT_TAGS_FAILURE } as const)

export const createVatTagsAttempt = () =>
  ({ type: VatTagActionEnum.CREATE_VAT_TAGS_ATTEMPT } as const)
export const createVatTagsReset = () =>
  ({ type: VatTagActionEnum.CREATE_VAT_TAGS_RESET } as const)
export const createVatTagsSuccess = () =>
  ({
    type: VatTagActionEnum.CREATE_VAT_TAGS_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "vat_tags.add-vat_tag.tooltip.success.title",
      bodyKey: "vat_tags.add-vat_tag.tooltip.success.message",
    },
  } as const)
export const createVatTagsPartialSuccess = (
  nameTag: string,
  vatType: string,
  reason: string
) =>
  ({
    type: VatTagActionEnum.CREATE_VAT_TAGS_PARTIAL_SUCCESS,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "vat_tags.add-vat_tag.tooltip.partial-success.title",
      bodyKey: `vat_tags.add-vat_tag.tooltip.partial-success.message.${vatType.toLocaleLowerCase()}`,
      bodyValues: { nameTag: nameTag, reason: reason },
    },
  } as const)

export const createVatTagsFailure = () =>
  ({
    type: VatTagActionEnum.CREATE_VAT_TAGS_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "vat_tags.add-vat_tag.tooltip.error.title",
      bodyKey: "vat_tags.add-vat_tag.tooltip.error.message",
    },
  } as const)
export const destroyVatTagAttempt = () =>
  ({
    type: VatTagActionEnum.DESTROY_VAT_TAG_ATTEMPT,
  } as const)
export const destroyVatTagSuccess = (isQuadratus = false) =>
  ({
    type: VatTagActionEnum.DESTROY_VAT_TAG_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: `${
        isQuadratus ? "quadratus_tags" : "vat_tags"
      }.destroy.tooltip.success.title`,
      bodyKey: `${
        isQuadratus ? "quadratus_tags" : "vat_tags"
      }.destroy.tooltip.success.message`,
    },
  } as const)
export const destroyVatTagFailure = (isQuadratus = false) =>
  ({
    type: VatTagActionEnum.DESTROY_VAT_TAG_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: `${
        isQuadratus ? "quadratus_tags" : "vat_tags"
      }.destroy.tooltip.error.title`,
      bodyKey: `${
        isQuadratus ? "quadratus_tags" : "vat_tags"
      }.destroy.tooltip.error.message`,
    },
  } as const)

export const getCompanyVatTagsAttempt = () =>
  ({ type: VatTagActionEnum.GET_COMPANY_VAT_TAGS_ATTEMPT } as const)
export const getCompanyVatTagsSuccess = (
  vatTags: GetVatTagsResponse,
  companyId: number
) =>
  ({
    type: VatTagActionEnum.GET_COMPANY_VAT_TAGS_SUCCESS,
    vatTags,
    companyId,
  } as const)
export const getCompanyVatTagsFailure = () =>
  ({ type: VatTagActionEnum.GET_COMPANY_VAT_TAGS_FAILURE } as const)
export const getQuadratusFiduciaryLabelTagsAttempt = () =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_ATTEMPT,
  } as const)
export const getQuadratusFiduciaryLabelTagsSuccess = (
  vatTags: GetVatTagsResponse
) =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_SUCCESS,
    vatTags,
  } as const)
export const getQuadratusFiduciaryLabelTagsFailure = () =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_FIDUCIARY_LABEL_TAGS_FAILURE,
  } as const)
export const getQuadratusCompanyLabelTagsAttempt = () =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_COMPANY_LABEL_TAGS_ATTEMPT,
  } as const)
export const getQuadratusCompanyLabelTagsSuccess = (
  vatTags: GetVatTagsResponse,
  companyId: number
) =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_COMPANY_LABEL_TAGS_SUCCESS,
    vatTags,
    companyId,
  } as const)
export const getQuadratusCompanyLabelTagsFailure = () =>
  ({
    type: VatTagActionEnum.GET_QUADRATUS_COMPANY_LABEL_TAGS_FAILURE,
  } as const)
export const createQuadratusLabelTagsAttempt = () =>
  ({ type: VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_ATTEMPT } as const)
export const createQuadratusLabelTagsSuccess = () =>
  ({
    type: VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "quadratus_tags.add-tag.tooltip.success.title",
      bodyKey: "quadratus_tags.add-tag.tooltip.success.message",
    },
  } as const)
export const createQuadratusLabelTagsPartialSuccess = (
  labelCode: string,
  label: string,
  buyOrSell: string,
  documentType: string,
  reason: string
) =>
  ({
    type: VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_PARTIAL_SUCCESS,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey:
        "quadratus_tags.add-tag.quadratus.tooltip.partial-success.title",
      bodyKey: `quadratus_tags.add-tag.quadratus.tooltip.partial-success.message`,
      bodyValues: {
        labelCode: labelCode,
        label: label,
        buyOrSell: buyOrSell,
        documentType: documentType,
        reason: reason,
      },
    },
  } as const)

export const createQuadratusLabelTagsFailure = () =>
  ({
    type: VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "quadratus_tags.add-tag.tooltip.error.title",
      bodyKey: "quadratus_tags.add-tag.tooltip.error.message",
    },
  } as const)

export type VatTagActionType = ReturnType<
  | typeof getFiduciaryVatTagsAttempt
  | typeof getFiduciaryVatTagsSuccess
  | typeof getFiduciaryVatTagsFailure
  | typeof createVatTagsAttempt
  | typeof createVatTagsReset
  | typeof createVatTagsSuccess
  | typeof createVatTagsFailure
  | typeof destroyVatTagAttempt
  | typeof destroyVatTagSuccess
  | typeof destroyVatTagFailure
  | typeof getQuadratusFiduciaryLabelTagsAttempt
  | typeof getQuadratusFiduciaryLabelTagsSuccess
  | typeof getQuadratusFiduciaryLabelTagsFailure
  | typeof getCompanyVatTagsAttempt
  | typeof getCompanyVatTagsSuccess
  | typeof getCompanyVatTagsFailure
  | typeof getQuadratusCompanyLabelTagsAttempt
  | typeof getQuadratusCompanyLabelTagsSuccess
  | typeof getQuadratusCompanyLabelTagsFailure
  | typeof createQuadratusLabelTagsAttempt
  | typeof createQuadratusLabelTagsPartialSuccess
  | typeof createQuadratusLabelTagsSuccess
  | typeof createQuadratusLabelTagsFailure
>

export interface VatTagsState {
  creation_status: "idle" | "success" | "error"
}

export const vatTagsInitialState: VatTagsState = {
  creation_status: "idle",
}

export function vatTagsReducer(
  state = vatTagsInitialState,
  action: VatTagActionType
): VatTagsState {
  switch (action.type) {
    case VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_ATTEMPT:
    case VatTagActionEnum.CREATE_VAT_TAGS_ATTEMPT: {
      return {
        ...state,
        creation_status: "idle",
      }
    }
    case VatTagActionEnum.CREATE_VAT_TAGS_RESET: {
      return {
        ...state,
        creation_status: "idle",
      }
    }
    case VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_SUCCESS:
    case VatTagActionEnum.CREATE_VAT_TAGS_SUCCESS: {
      return {
        ...state,
        creation_status: "success",
      }
    }
    case VatTagActionEnum.CREATE_QUADRATUS_LABEL_TAGS_FAILURE:
    case VatTagActionEnum.CREATE_VAT_TAGS_FAILURE: {
      return {
        ...state,
        creation_status: "error",
      }
    }

    default:
      return state
  }
}

export const getFiduciaryVatTagsThunk =
  (fiduciaryId: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getFiduciaryVatTagsAttempt())
    return axios
      .get<GetVatTagsResponse>(
        `fiduciaries/${fiduciaryId}/get_fiduciary_vat_tags`
      )
      .then((response) => {
        dispatch(getFiduciaryVatTagsSuccess(response.data))
      })
      .catch((e) => {
        dispatch(getFiduciaryVatTagsFailure())
      })
  }

export const getCompanyVatTagsThunk =
  (companyId: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getCompanyVatTagsAttempt())
    return axios
      .get<GetVatTagsResponse>(`companies/${companyId}/get_company_vat_tags`)
      .then((response) => {
        dispatch(getCompanyVatTagsSuccess(response.data, companyId))
      })
      .catch((e) => {
        dispatch(getCompanyVatTagsFailure())
      })
  }

export const createVatTagsThunk =
  (fiduciaryId: number, vatTags: CreateVatTagPayload[], companyId?: number) =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    const route = companyId
      ? `companies/${companyId}/create_company_vat_tags`
      : `fiduciaries/${fiduciaryId}/create_fiduciary_vat_tags`

    dispatch(createVatTagsAttempt())
    return axios
      .post<CreateVatTagResponse>(`${route}`, { vat_tags: vatTags })
      .then(({ status, data }: AxiosResponse<CreateVatTagResponse>) => {
        if (status === 207 && Array.isArray(data)) {
          data.forEach(({ message, data }) => {
            const messages: Record<string, string> = {
              already_exist:
                "Un label de TVA existe déjà pour ce type de TVA et ce taux",
              too_long: "Le label de TVA est trop long",
              unknown_vat_type: "Type de TVA inconnue",
              not_empty: "Le code ne peut pas être vide ",
            }
            const i18Display =
              messages[message] ||
              "Une erreur est survenue, veuillez contacter Chaintrust"
            dispatch(
              createVatTagsPartialSuccess(
                data.name_tag,
                data.vat_type,
                i18Display
              )
            )
          })
          if (data.length < vatTags.length) dispatch(createVatTagsSuccess())
        } else if (status === 200) {
          dispatch(createVatTagsSuccess())
          dispatch(
            companyId
              ? getCompanyVatTagsThunk(companyId)
              : getFiduciaryVatTagsThunk(fiduciaryId)
          )
        }
      })
      .catch((e) => {
        dispatch(createVatTagsFailure())
        companyId
          ? dispatch(getCompanyVatTagsThunk(companyId))
          : dispatch(getFiduciaryVatTagsThunk(fiduciaryId))
      })
  }

export const destroyVatTagThunk =
  (
    fiduciaryId: number,
    vat_tag_id: number,
    accountingSoftware: AccountingSoftware,
    companyId?: number
  ) =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    const route = companyId
      ? `companies/${companyId}/destroy_company_vat_tag/${vat_tag_id}`
      : `fiduciaries/${fiduciaryId}/destroy_fiduciary_vat_tag/${vat_tag_id}`
    const isQuadratus = accountingSoftware === "quadratus"

    dispatch(destroyVatTagAttempt())
    return axios
      .delete(route)
      .then(() => {
        dispatch(destroyVatTagSuccess(isQuadratus))
        if (isQuadratus)
          dispatch(
            companyId
              ? getQuadratusCompanyLabelTagsThunk(companyId)
              : getQuadratusFiduciaryLabelTagsThunk(fiduciaryId)
          )
        else
          dispatch(
            companyId
              ? getCompanyVatTagsThunk(companyId)
              : getFiduciaryVatTagsThunk(fiduciaryId)
          )
      })
      .catch((e) => {
        dispatch(destroyVatTagFailure(isQuadratus))
        companyId
          ? dispatch(getCompanyVatTagsThunk(companyId))
          : dispatch(getFiduciaryVatTagsThunk(fiduciaryId))
      })
  }

export const getQuadratusCompanyLabelTagsThunk =
  (companyId: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getQuadratusCompanyLabelTagsAttempt())
    return axios
      .get<GetVatTagsResponse>(
        `companies/${companyId}/get_quadratus_label_tags`
      )
      .then((response) => {
        dispatch(getQuadratusCompanyLabelTagsSuccess(response.data, companyId))
      })
      .catch((e) => {
        dispatch(getQuadratusCompanyLabelTagsFailure())
      })
  }

export const getQuadratusFiduciaryLabelTagsThunk =
  (fiduciaryId: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getQuadratusFiduciaryLabelTagsAttempt())
    return axios
      .get<GetVatTagsResponse>(
        `fiduciaries/${fiduciaryId}/get_quadratus_label_tags`
      )
      .then((response) => {
        dispatch(getQuadratusFiduciaryLabelTagsSuccess(response.data))
      })
      .catch((e) => {
        dispatch(getQuadratusFiduciaryLabelTagsFailure())
      })
  }

export const createQuadratusLabelTagThunk =
  (
    fiduciaryId: number,
    labelTags: CreateLabelTagPayload[],
    companyId?: number
  ) =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    const route = companyId
      ? `companies/${companyId}/create_quadratus_label_tags`
      : `fiduciaries/${fiduciaryId}/create_quadratus_label_tags`

    dispatch(createQuadratusLabelTagsAttempt())
    return axios
      .post<CreateVatTagResponse>(`${route}`, { label_tags: labelTags })
      .then(({ status, data }: AxiosResponse<CreateVatTagResponse>) => {
        if (status === 207 && Array.isArray(data)) {
          data.forEach(({ message, data }) => {
            const messages: Record<string, string> = {
              code_too_long:
                "Le code est trop long, la limite est de 1 caractère",
              label_too_long:
                "Le label est trop long, la limite est de 20 caractères",
              unknown_buy_or_sell:
                "Le code doit être paramétré pour l'achat ou la vente",
              unknown_document_type:
                "Le code doit être paramétré pour les factures ou avoirs",
              already_exist:
                "Il existe déjà un tag pour ce type de document en achat ou vente",
              label_empty: "Le label ne doit pas être vide",
            }

            const i18Display =
              messages[message] ||
              "Une erreur est survenue, veuillez contacter Chaintrust"
            dispatch(
              createQuadratusLabelTagsPartialSuccess(
                data.label_code,
                data.label,
                data.buy_or_sell === "buy" ? "Achat" : "Vente",
                data.document_type === "invoice" ? "Facture" : "Avoir",
                i18Display
              )
            )
          })
          if (data.length < labelTags.length)
            dispatch(createQuadratusLabelTagsSuccess())
        } else if (status === 200) {
          dispatch(createQuadratusLabelTagsSuccess())
          dispatch(
            companyId
              ? getQuadratusCompanyLabelTagsThunk(companyId)
              : getQuadratusFiduciaryLabelTagsThunk(fiduciaryId)
          )
        }
      })
      .catch((e) => {
        dispatch(createQuadratusLabelTagsFailure())
        companyId
          ? dispatch(getQuadratusCompanyLabelTagsThunk(companyId))
          : dispatch(getQuadratusFiduciaryLabelTagsThunk(fiduciaryId))
      })
  }
